/**
 * Everything the app needs to reach the account, injected at build time. Absent in
 * `check-web` builds and in the fixture-only demo screens, so absence is a state, not an error.
 */
/** Paddle.js's client-side token is public by design; absent means no checkout can open. */
export type PaddleConfig = { token: string; environment: "sandbox" | "production" };

export type AppConfig = {
  apiUrl: string;
  issuer: string;
  clientId: string;
  loginDomain: string;
  paddle: PaddleConfig | null;
  trialOpen: boolean;
};

export function readConfig(env: Record<string, string | undefined>): AppConfig | null {
  const apiUrl = env.VITE_API_URL;
  const issuer = env.VITE_COGNITO_ISSUER;
  const clientId = env.VITE_COGNITO_CLIENT_ID;
  const loginDomain = env.VITE_COGNITO_LOGIN_DOMAIN;
  if (!apiUrl || !issuer || !clientId || !loginDomain) return null;
  const paddleToken = env.VITE_PADDLE_TOKEN;
  const paddle: PaddleConfig | null = paddleToken
    ? {
        token: paddleToken,
        environment: env.VITE_PADDLE_ENV === "production" ? "production" : "sandbox",
      }
    : null;
  // The trial and checkout render only when the build says so; the closed beta runs with
  // it off and the code field is the whole chooser (spec §5). Flipped in scripts/web-env.sh.
  const trialOpen = env.VITE_TRIAL_OPEN === "true";
  return {
    apiUrl: apiUrl.replace(/\/$/, ""),
    issuer,
    clientId,
    loginDomain: loginDomain.replace(/\/$/, ""),
    paddle,
    trialOpen,
  };
}

export const appConfig: AppConfig | null = readConfig(
  import.meta.env as Record<string, string | undefined>,
);
